// RequireRole — dashboard route gate.
//
// Supports two modes:
//   - **permission-based** (Custom RBAC §6, preferred): pass `permissions`
//     prop with a list; ANY match grants access. Empty list = any
//     authenticated session passes.
//   - **role-based** (legacy): pass `roles` prop with role strings.
//
// When BOTH are passed, `permissions` wins (defense-in-depth — sub-API
// requirePermission gates would also reject). Use canonical source:
// `Rbac.DASHBOARD_ROUTE_PERMISSIONS[route]`.

import React from "react";
import { Navigate } from "react-router-dom";
import { useDashboardSession, useDashboardSessionHydrating } from "./useDashboardSession.js";

export interface RequireRoleProps {
  /** Legacy role list — pass this OR `permissions`, not both required. */
  roles?: readonly string[];
  /** Required permission ids — ANY match grants access. Empty array = any
   *  authenticated session passes. Preferred over `roles`. */
  permissions?: readonly string[];
  /** Defense-in-depth scope requirement (legacy; permissions cover this
   *  in the new model). Kept for transitional routes. */
  scopes?: readonly string[];
  children: React.ReactNode;
  /** Override redirect path; default `/dashboard/forbidden`. */
  redirectTo?: string;
}

export function RequireRole({
  roles,
  permissions,
  scopes,
  children,
  redirectTo = "/dashboard/forbidden",
}: RequireRoleProps): React.ReactElement {
  const session = useDashboardSession();
  const isHydrating = useDashboardSessionHydrating();
  const role = session.role;

  // Hold off on the role decision while /v1/auth/me is in flight on a
  // fresh post-OAuth load. Without this gate the synchronous initial
  // state (DEFAULT_GUEST) trips RequireRole, redirects to /forbidden,
  // and even though the session upgrades to super_admin a moment later
  // the URL stays at /forbidden — the user sees a guest forbidden page
  // when their session is actually fine.
  if (isHydrating) {
    return <HydratingFallback />;
  }

  // ── Permission-based check (preferred path) ──
  if (permissions !== undefined) {
    // Empty list = any authenticated session passes (e.g. /settings).
    if (permissions.length > 0) {
      // super_admin god mode: their session.permissions includes every
      // permission, so any required[] match would pass anyway. Explicit
      // check for the not-yet-hydrated case where super_admin flag is
      // true but permissions hasn't loaded.
      const isSuperAdminBypass = session.super_admin === true && !session.acting_tenant_id;
      if (!isSuperAdminBypass) {
        const have = new Set(session.permissions);
        const matched = permissions.some((p) => have.has(p));
        if (!matched) {
          return <Navigate to={`${redirectTo}?missing=${encodeURIComponent(permissions[0])}`} replace />;
        }
      }
    }
  } else if (roles !== undefined) {
    // ── Legacy role-based check ──
    if (!roles.includes(role)) {
      return <Navigate to={`${redirectTo}?from=${encodeURIComponent(role)}`} replace />;
    }
  }

  if (scopes && scopes.length > 0) {
    const have = new Set(session.scopes);
    // admin:platform (super_admin) and admin:tenant are wildcard short-circuits
    // for the runtime scope check; admin:platform also passes the platform-level
    // scopes that admin:tenant cannot — see packages/shared/src/rbac/index.ts.
    if (!have.has("admin:platform") && !have.has("admin:tenant")) {
      for (const s of scopes) {
        if (!have.has(s)) {
          return <Navigate to={`${redirectTo}?missing=${encodeURIComponent(s)}`} replace />;
        }
      }
    }
  }
  return <>{children}</>;
}

// ── Hydrating fallback ────────────────────────────────────────────────
// Tiny centered spinner shown while /v1/auth/me is in flight on the
// initial bootstrap. Keeps layout stable + matches dashboard dark theme.
function HydratingFallback(): React.ReactElement {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" aria-live="polite">
      <div className="flex flex-col items-center gap-3 text-text-secondary">
        <span
          className="h-8 w-8 rounded-full border-2 border-border border-t-accent animate-spin"
          aria-hidden
        />
        <span className="text-sm">驗證身分中…</span>
      </div>
    </div>
  );
}
